import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import {
  RadioGroupMinimal,
  Radio,
} from '@lifeomic/chroma-react/components/Radio';
import moment from 'moment';
import { PrimarySurvey } from '../../../Surveys/util';
import SurveyResponse from '../../../Surveys/util/SurveyResponse';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { SurveyTemplateWithId } from '../../../../services/SurveyService';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Checkbox } from '@lifeomic/chroma-react/components/Checkbox';
import { SurveyResultsFilters } from './useSurveyResults';

const messages = defineMessages({
  survey: {
    id: 'SelectorPanel.survey',
    defaultMessage: 'Survey',
  },
  surveyResponse: {
    id: 'SelectorPanel.surveyResponse',
    defaultMessage: 'Response',
  },
  completedBy: {
    id: 'SelectorPanel.completedBy',
    defaultMessage: 'Completed by: {author}',
  },
  template: {
    id: 'SelectorPanel.template',
    defaultMessage: 'Template',
  },
  noTemplate: {
    id: 'SelectorPanel.template',
    defaultMessage: 'No Template',
  },
  hideDisplayOnlyContent: {
    id: 'SelectorPanel.hideDisplayOnlyContent',
    defaultMessage: 'Hide display-only content',
  },
  hideDisplayOnlyContentHelp: {
    id: 'SelectorPanel.hideDisplayOnlyContentHelp',
    defaultMessage: 'Hides informational text, image, and course content',
  },
  showOnlyFavorites: {
    id: 'SelectorPanel.showOnlyFavorites',
    defaultMessage: 'Show Only Favorites',
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    borderRight: `solid ${theme.pxToRem(1)} ${theme.palette.divider}`,
    overflow: 'auto',
    maxWidth: theme.pxToRem(288),
    minWidth: theme.pxToRem(288),
  },
}));

export type SelectorPanelOwnProps = {
  primarySurveys: PrimarySurvey[];
  selectPrimarySurveyId: (primarySurveyId: string) => void;
  selectableResponses: SurveyResponse[];
  setResponseId: (responsesId: string) => void;
  selectedPrimarySurveyId: string;
  selectedResponseId: string;
  author: string | null;
  selectableTemplates: SurveyTemplateWithId[];
  selectedTemplate: SurveyTemplateWithId | null;
  setSurveyTemplate: (template: SurveyTemplateWithId | null) => void;
  filters: SurveyResultsFilters;
  onFiltersChange: (filters: SurveyResultsFilters) => void;
};

export type SelectorPanelProps = SelectorPanelOwnProps;

const SelectorPanel: React.FC<SelectorPanelProps> = ({
  primarySurveys,
  selectPrimarySurveyId,
  selectableResponses,
  setResponseId,
  selectedPrimarySurveyId,
  selectedResponseId,
  author,
  selectableTemplates,
  selectedTemplate,
  setSurveyTemplate,
  filters,
  onFiltersChange,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  return (
    <FormBox
      fullHeight
      direction="column"
      justify="flex-start"
      className={classes.root}
      padding={2}
    >
      <Select
        label={formatMessage(messages.surveyResponse)}
        value={selectedResponseId}
        onChange={setResponseId}
        disabled={!selectableResponses}
        helpMessage={
          author ? formatMessage(messages.completedBy, { author }) : undefined
        }
        fullWidth
      >
        {[...(selectableResponses || [])].map(_ => (
          <SelectOption
            key={_.id}
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            value={_.id}
            title={moment(_.authoredDate).format('lll')}
          />
        ))}
      </Select>
      <Box width="100%">
        <RadioGroupMinimal
          key={selectedPrimarySurveyId}
          title={formatMessage(messages.survey)}
          value={selectedPrimarySurveyId}
          onChange={e => selectPrimarySurveyId(e?.target?.value)}
          direction="column"
          fullWidth
        >
          {primarySurveys.map(_ => (
            <Radio
              key={_.primarySurveyId}
              value={_.primarySurveyId}
              label={_.latestSurvey.title}
            />
          ))}
        </RadioGroupMinimal>
      </Box>
      <Checkbox
        checked={filters.hideDisplayOnlyContent}
        onChange={e =>
          onFiltersChange({
            ...filters,
            hideDisplayOnlyContent: e.target.checked,
          })
        }
        label={formatMessage(messages.hideDisplayOnlyContent)}
        helpMessage={formatMessage(messages.hideDisplayOnlyContentHelp)}
      />
      <Checkbox
        checked={filters.showFavorites}
        onChange={e =>
          onFiltersChange({
            ...filters,
            showFavorites: e.target.checked,
          })
        }
        label={formatMessage(messages.showOnlyFavorites)}
      />
      {selectableTemplates.length ? (
        <Select
          label={formatMessage(messages.template)}
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          value={selectedTemplate?.templateId || null}
          onChange={(templateId: string) => {
            if (!templateId) {
              setSurveyTemplate(null);
              return;
            }

            const targetTemplate = selectableTemplates.find(
              _ => _.templateId === templateId,
            );
            if (targetTemplate) {
              setSurveyTemplate(targetTemplate);
            }
          }}
          fullWidth
        >
          <SelectOption value="">
            <em>{formatMessage(messages.noTemplate)}</em>
          </SelectOption>
          {[...(selectableTemplates || [])].map(_ => (
            <SelectOption
              key={_.templateId}
              value={_.templateId}
              title={_.name}
            />
          ))}
        </Select>
      ) : null}
    </FormBox>
  );
};

export default React.memo(SelectorPanel);
